import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { cfpBaseUrl } from '../../network/urls';

const initialState = {
    data: [],
    isLoading: false,
    error: null,
};

const getFeedersCache = new Map();
const getFeedersInflight = new Map();
let officeStatusLockKey = '';
let feederListFetchActive = false;

export const setFeederListFetchActive = (active) => {
    feederListFetchActive = !!active;
};

export const getDiscoCodeFromOffice = (officeCode) => {
    const companyCode = `${String(officeCode ?? '').trim().slice(0, 2)}000`;
    return companyCode === '10000' ? '15000' : companyCode;
};

export const resolveFeederListOfficeCode = (officeCode, authCode, hierarchy) => {
    const raw = String(officeCode || authCode || '').replace(/\D/g, '');
    const level = Number(hierarchy);
    if (level === 1 || level === 2) return raw.slice(0, 2);
    if (level === 3) return raw.slice(0, 3);
    if (level === 4) return raw.slice(0, 4);
    if (level === 5 || level === 51 || level === 52) return raw.slice(0, 5);
    if (raw.length >= 5) return raw.slice(0, 5);
    if (raw.length === 4) return raw.slice(0, 4);
    if (raw.length === 3) return raw.slice(0, 3);
    return raw.slice(0, 2);
};

const parseGetFeedersRows = (result) => {
    if (Array.isArray(result?.data)) return result.data;
    if (Array.isArray(result?.data?.data)) return result.data.data;
    if (Array.isArray(result?.feeders)) return result.feeders;
    if (Array.isArray(result)) return result;
    return [];
};

const feederCodeFromRow = (row) =>
    String(
        row?.FEEDERCODE
        ?? row?.feederCode
        ?? row?.feeder_code
        ?? row?.FeederCode
        ?? '',
    ).trim();

const normalizeFeederCode = (value) => {
    const raw = String(value ?? '').trim();
    if (!raw) return '';
    return raw.replace(/^0+/, '') || '0';
};

const mergeCatalogWithStatus = (catalog, statusRows) => {
    const statusByCode = new Map();
    (statusRows || []).forEach((row) => {
        const key = normalizeFeederCode(
            row?.feeder_code ?? row?.feederCode ?? row?.FEEDERCODE,
        );
        if (key) statusByCode.set(key, row);
    });

    const merged = [];
    const seen = new Set();
    (catalog || []).forEach((row) => {
        const rawCode = feederCodeFromRow(row);
        const key = normalizeFeederCode(rawCode);
        if (!key || seen.has(key)) return;
        seen.add(key);
        const status = statusByCode.get(key) || {};
        merged.push({
            ...status,
            feeder_code: status.feeder_code || status.feederCode || rawCode,
            feeder: status.feeder || status.feeder_name || row.FEEDERNAME || rawCode,
            sDivCode: String(row?.SDIVCODE ?? row?.sDivCode ?? '').trim(),
        });
    });
    return merged;
};

export const fetchFeedersBySDivCode = async (token, officeCode) => {
    const key = String(officeCode ?? '').replace(/\D/g, '');
    if (!key) return [];
    if (getFeedersCache.has(key)) return getFeedersCache.get(key);
    if (getFeedersInflight.has(key)) return getFeedersInflight.get(key);

    const request = (async () => {
        const formdata = new FormData();
        formdata.append('sDivCode', key);
        const myHeaders = new Headers();
        myHeaders.append('Authorization', token);
        const response = await fetch(`${cfpBaseUrl}demandNotice/getFeeders`, {
            method: 'POST',
            headers: myHeaders,
            body: formdata,
            redirect: 'follow',
        });
        const result = await response.json();
        const rows = parseGetFeedersRows(result);
        if (rows.length) getFeedersCache.set(key, rows);
        return rows;
    })();

    getFeedersInflight.set(key, request);
    try {
        return await request;
    } finally {
        getFeedersInflight.delete(key);
    }
};

const parseFeederStatusRows = (result) => {
    const rows = result?.[0]?.response?.data;
    if (Array.isArray(rows)) return rows;
    if (Array.isArray(result?.data)) return result.data;
    if (Array.isArray(result)) return result;
    return [];
};

const uniqueStatusRows = (rows = []) =>
    Array.from(
        rows.reduce((acc, feeder) => {
            const rowKey = String(
                feeder?.feeder_code ?? feeder?.feederCode ?? feeder?.FEEDERCODE ?? '',
            ).trim();
            if (rowKey) {
                acc.set(rowKey, {
                    ...feeder,
                    feeder_code: feeder?.feeder_code ?? feeder?.feederCode ?? feeder?.FEEDERCODE ?? rowKey,
                    feeder: feeder?.feeder ?? feeder?.feeder_name ?? feeder?.FEEDERNAME ?? rowKey,
                });
            }
            return acc;
        }, new Map()).values(),
    );

export const postGetFeederStatus = async (token, codes, discoCode) => {
    const uniqueCodes = [...new Set((codes || []).map((item) => String(item).trim()).filter(Boolean))];
    if (!uniqueCodes.length) return [];

    const myHeaders = new Headers();
    myHeaders.append('Authorization', token);
    myHeaders.append('Content-Type', 'application/json');
    const response = await fetch(`${cfpBaseUrl}lineComplaints/GetFeederStatus`, {
        method: 'POST',
        headers: myHeaders,
        body: JSON.stringify({
            code: uniqueCodes,
            disco_code: discoCode,
        }),
        redirect: 'follow',
    });
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Feeder Status API issue!');
    }
    return uniqueStatusRows(parseFeederStatusRows(await response.json()));
};

export const releaseFeederOfficeFetchLock = () => {
    officeStatusLockKey = '';
};

export const fetchFeederStatus = createAsyncThunk(
    'complaints/fetchFeederStatusArray',
    async (feederString, { getState }) => {
        const { auth } = getState();
        const { code, token } = auth;
        const codes = String(feederString || '')
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean);
        if (!codes.length) return [];
        return uniqueStatusRows(
            await postGetFeederStatus(token, codes, getDiscoCodeFromOffice(code)),
        );
    },
    {
        condition: () => !feederListFetchActive,
    },
);

export const fetchFeederStatusByOffice = createAsyncThunk(
    'complaints/fetchFeederStatusByOffice',
    async ({ officeCode, hierarchy } = {}, { getState }) => {
        const { auth } = getState();
        const { token, code } = auth;
        const scopeCode = resolveFeederListOfficeCode(officeCode, code, hierarchy ?? auth?.hierarchy);
        const catalog = await fetchFeedersBySDivCode(token, scopeCode);
        const feederCodes = [...new Set((catalog || []).map(feederCodeFromRow).filter(Boolean))];
        if (!feederCodes.length) return [];
        const statusRows = await postGetFeederStatus(
            token,
            feederCodes,
            getDiscoCodeFromOffice(scopeCode),
        );
        return mergeCatalogWithStatus(catalog, statusRows);
    },
    {
        condition: ({ officeCode, hierarchy } = {}, { getState }) => {
            const { auth } = getState();
            const key = resolveFeederListOfficeCode(officeCode, auth?.code, hierarchy ?? auth?.hierarchy);
            if (!key) return false;
            if (officeStatusLockKey) return false;
            officeStatusLockKey = key;
            return true;
        },
    },
);

const feederStatusSlice = createSlice({
    name: 'feederStatus',
    initialState,
    reducers: {
        resetFeederStatusState(state) {
            state.data = [];
            state.isLoading = false;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        const pending = (state) => {
            state.isLoading = true;
        };
        const fulfilled = (state, action) => {
            state.isLoading = false;
            if (Array.isArray(action.payload)) {
                state.data = action.payload;
            }
            state.error = null;
        };
        const rejected = (state, action) => {
            state.isLoading = false;
            state.error = action.error.message;
        };

        builder
            .addCase(fetchFeederStatus.pending, pending)
            .addCase(fetchFeederStatus.fulfilled, fulfilled)
            .addCase(fetchFeederStatus.rejected, rejected)
            .addCase(fetchFeederStatusByOffice.pending, pending)
            .addCase(fetchFeederStatusByOffice.fulfilled, fulfilled)
            .addCase(fetchFeederStatusByOffice.rejected, rejected);
    },
});

export const { resetFeederStatusState } = feederStatusSlice.actions;

export default feederStatusSlice.reducer;
